// =============================================================================
// setup — RPC wrappers for the /setup page (experiences, schedule, overrides)
// =============================================================================
// Post-0048 the schedule model is per-LOCATION (continuous opening hours with
// optional breaks) and the experience↔location link is explicit + editable
// from both sides.
// =============================================================================
import { supabase } from './supabase';

// ---- Experience metadata (catalog editor in setup) ------------------------
export interface ExperienceFullRow {
  id: string;
  name: string;                            // internal name (read-only here)
  display_name: string | null;             // NL display copy
  booking_slug: string | null;
  booking_base_price_cents: number;
  duration_minutes: number;
  booking_image_url: string | null;
}

export async function listExperiencesFull(): Promise<ExperienceFullRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_experiences_full');
  if (error) throw error;
  return (data as ExperienceFullRow[]) ?? [];
}

export interface EditExperienceInput {
  experienceId: string;
  display_name?: string | null;
  booking_base_price_cents?: number | null;
  duration_minutes?: number | null;
  booking_image_url?: string | null;
  booking_slug?: string | null;
}

export async function editExperience(
  operatorId: string,
  input: EditExperienceInput,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_edit_experience', {
      p_operator_id:               operatorId,
      p_experience_id:             input.experienceId,
      p_display_name:              input.display_name ?? null,
      p_booking_base_price_cents:  input.booking_base_price_cents ?? null,
      p_duration_minutes:          input.duration_minutes ?? null,
      p_booking_image_url:         input.booking_image_url ?? null,
      p_booking_slug:              input.booking_slug ?? null,
    });
  if (error) throw error;
}

// ---- Per-language name & description (experience_language_support) --------
// One row per language in public.languages; display_name/description come from
// the canonical (lowest-priority) support row, or null when the experience has
// no row for that language yet. The widget shows display_name (required) +
// description per locale via booking-config's locale resolution.
export interface ExperienceLanguageRow {
  language_id:   string;
  short_name:    string;         // uppercase, e.g. 'NL' / 'EN'
  language_name: string;
  display_name:  string | null;
  description:   string | null;
}

export async function getExperienceLanguages(
  experienceId: string,
): Promise<ExperienceLanguageRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_get_experience_languages', { p_experience_id: experienceId });
  if (error) throw error;
  return ((data as { languages?: ExperienceLanguageRow[] } | null)?.languages) ?? [];
}

export interface ExperienceLanguageInput {
  language_id:  string;
  display_name: string | null;   // empty/null clears the widget name for this locale
  description:  string | null;
}

export async function saveExperienceLanguages(
  operatorId:   string,
  experienceId: string,
  rows:         ExperienceLanguageInput[],
): Promise<{ saved: boolean; rows_saved: number }> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_save_experience_languages', {
      p_operator_id:   operatorId,
      p_experience_id: experienceId,
      p_rows:          rows,
    });
  if (error) throw error;
  return data as { saved: boolean; rows_saved: number };
}

// ---- Per-location schedule (0050) ----------------------------------------
// One rule per row in fn_admin_get_location_schedule. Each rule has a date
// range, weekday selection, and 1+ open periods (with optional break in
// between rendered as a closed gap).
export interface LocationSchedulePeriod {
  opens_at:  string;   // 'HH:MM'
  closes_at: string;   // 'HH:MM'
}
export interface LocationScheduleRule {
  id:           string;
  day_of_week:  number[];
  valid_from:   string; // 'YYYY-MM-DD'
  valid_until:  string; // 'YYYY-MM-DD'
  is_override:  boolean;
  periods:      LocationSchedulePeriod[];
}
export interface LocationScheduleSnapshot {
  location_id: string;
  rules:       LocationScheduleRule[];
}

export async function getLocationSchedule(
  locationId: string,
): Promise<LocationScheduleSnapshot> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_get_location_schedule', { p_location_id: locationId });
  if (error) throw error;
  return (data as LocationScheduleSnapshot) ?? { location_id: locationId, rules: [] };
}

export interface LocationScheduleRuleInput {
  day_of_week:  number[];
  valid_from:   string;
  valid_until:  string;
  is_override:  boolean;
  periods:      LocationSchedulePeriod[];
}

export async function saveLocationSchedule(
  operatorId: string,
  locationId: string,
  rules:      LocationScheduleRuleInput[],
): Promise<{ saved: boolean; rules_count: number; new_rule_ids: string[] }> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_save_location_schedule', {
      p_operator_id: operatorId,
      p_location_id: locationId,
      p_rules:       rules,
    });
  if (error) throw error;
  return data as { saved: boolean; rules_count: number; new_rule_ids: string[] };
}

// ---- Per-location experiences (location-side primary editor) -------------
export interface LocationExperienceRoomLite {
  id:           string;
  name:         string;
  max_capacity: number;
}
// status: 'active' = bookable + visible in the widget; 'coming_soon' =
// visible with a badge but NOT bookable (release_estimate / info_url dress
// the widget card); 'inactive' = hidden legacy rows (never written by save).
export type LocationExperienceStatus = 'active' | 'coming_soon' | 'inactive';

export interface LocationExperienceLink {
  id:               string;          // booking.location_experience.id
  experience_id:    string;
  experience_name:  string;
  duration_minutes: number;
  valid_from:       string;
  valid_until:      string;
  is_active:        boolean;
  status:           LocationExperienceStatus;
  release_estimate: string | null;   // customer-facing free text (EN for now)
  info_url:         string | null;   // click-through page for the widget card
  room_ids:         string[];        // [] = all bookable rooms at the location
  sort_order:       number;          // widget picker order (persisted as array index on save)
}
export interface LocationExperiencesSnapshot {
  location_id: string;
  rooms:       LocationExperienceRoomLite[];
  experiences: LocationExperienceLink[];
}

export async function getLocationExperiences(
  locationId: string,
): Promise<LocationExperiencesSnapshot> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_get_location_experiences', { p_location_id: locationId });
  if (error) throw error;
  return (data as LocationExperiencesSnapshot) ?? {
    location_id: locationId,
    rooms:       [],
    experiences: [],
  };
}

export interface LocationExperienceLinkInput {
  experience_id:    string;
  valid_from:       string;
  valid_until:      string;
  status:           'active' | 'coming_soon';
  release_estimate: string | null; // only kept server-side when coming_soon
  info_url:         string | null; // must be http(s) when set
  room_ids:         string[]; // empty array = all rooms
}

export async function saveLocationExperiences(
  operatorId: string,
  locationId: string,
  links:      LocationExperienceLinkInput[],
): Promise<{ saved: boolean; links_count: number }> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_save_location_experiences', {
      p_operator_id: operatorId,
      p_location_id: locationId,
      p_links:       links,
    });
  if (error) throw error;
  return data as { saved: boolean; links_count: number };
}

// ---- Experience-side mirror -----------------------------------------------
export interface ExperienceLocationLink {
  id:                string;
  location_id:       string;
  location_name:     string;
  valid_from:        string;
  valid_until:       string;
  is_active:         boolean;
  status:            LocationExperienceStatus;
  release_estimate:  string | null;
  info_url:          string | null;
  room_ids:          string[];
  rooms_at_location: Array<{ id: string; name: string }>;
}
export interface ExperienceLocationsSnapshot {
  experience_id: string;
  links:         ExperienceLocationLink[];
}

export async function getExperienceLocations(
  experienceId: string,
): Promise<ExperienceLocationsSnapshot> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_get_experience_locations', { p_experience_id: experienceId });
  if (error) throw error;
  return (data as ExperienceLocationsSnapshot) ?? {
    experience_id: experienceId,
    links:         [],
  };
}

// The save RPC is replace-all, so the mirror editor must round-trip
// status/release_estimate/info_url even though it has no UI for them —
// otherwise saving locations from the experience side would wipe the
// coming-soon settings made in the location-side editor.
export interface ExperienceLocationLinkInput {
  location_id:      string;
  valid_from:       string;
  valid_until:      string;
  status:           'active' | 'coming_soon';
  release_estimate: string | null;
  info_url:         string | null;
  room_ids:         string[];
}

export async function setExperienceLocations(
  operatorId:   string,
  experienceId: string,
  links:        ExperienceLocationLinkInput[],
): Promise<{ saved: boolean; links_count: number }> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_set_experience_locations', {
      p_operator_id:   operatorId,
      p_experience_id: experienceId,
      p_links:         links,
    });
  if (error) throw error;
  return data as { saved: boolean; links_count: number };
}

// ---- Overrides (unchanged from pre-0048) ----------------------------------
export type OverrideKind = 'blocked' | 'reduced_capacity';

// experienceId === null → room-wide override
// experienceId === string → applies only to that experience
export async function setScheduleOverride(
  operatorId: string,
  roomId: string,
  slotStart: string,                       // ISO timestamptz
  kind: OverrideKind | null,               // null = DELETE
  overrideCapacity: number | null = null,
  reason: string | null = null,
  experienceId: string | null = null,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_set_schedule_override', {
      p_operator_id:       operatorId,
      p_room_id:           roomId,
      p_slot_start:        slotStart,
      p_kind:              kind,
      p_override_capacity: overrideCapacity,
      p_reason:            reason,
      p_experience_id:     experienceId,
    });
  if (error) throw error;
}

export interface OverrideRow {
  room_id: string;
  slot_start: string;
  experience_id: string | null;
  kind: OverrideKind;
  override_capacity: number | null;
  reason: string | null;
}

export async function listScheduleOverrides(
  roomId: string,
  fromIso: string,
  toIso: string,
): Promise<OverrideRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .from('schedule_overrides')
    .select('room_id, slot_start, experience_id, kind, override_capacity, reason')
    .eq('room_id', roomId)
    .gte('slot_start', fromIso)
    .lt('slot_start', toIso);
  if (error) throw error;
  return (data ?? []) as OverrideRow[];
}

// ---- Admin /setup Locations tab -------------------------------------------
export interface AdminLocationRow {
  id:                    string;
  name:                  string;
  slug:                  string | null;  // widget deep-link handle (?location=)
  booking_display_name:  string | null;
  booking_number_prefix: string;        // 'XXX' until configured
  booking_enabled:       boolean;
  booking_listed:        boolean;       // shown in the public widget picker (vs. bookable-only-via-link)
  booking_image_url:     string | null; // widget location-picker card photo
  address_line:          string | null;
  postal_code:           string | null;
  city:                  string | null;
  country_code:          string;
  currency:              string;
  vat_rate_pct:          number;
  // Stripe Terminal reader for in-store POS payments (walk-in "Pinterminal
  // (POS)", pay-and-check-in, kiosk fallback). null = no terminal.
  pos_reader_id:         string | null;
  pos_reader_label:      string | null;
  // This venue's review links, one per site. Each venue is its own Google
  // Business Profile and its own Tripadvisor entry, so both live per location.
  // Used by the post-visit review mail as {{google review}} / {{trip review}};
  // null = no link, and that token renders empty.
  google_review_url:      string | null;
  tripadvisor_review_url: string | null;
  // Optional .com override, used for locale 'en' recipients. Tripadvisor
  // splits language by domain and .com does not redirect Dutch browsers, so
  // one URL cannot serve both. null = everyone gets the default.
  tripadvisor_review_url_en: string | null;
}

export async function listAdminLocations(): Promise<AdminLocationRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_locations');
  if (error) throw error;
  return (data as AdminLocationRow[]) ?? [];
}

export interface EditLocationInput {
  locationId:            string;
  // NULL for any field = leave unchanged.
  booking_enabled?:       boolean | null;
  // Visible in the public widget picker. false = bookable but hidden, reachable
  // only via the ?location= bypass link.
  booking_listed?:        boolean | null;
  booking_number_prefix?: string | null;
  // empty string clears back to NULL on the server (booking-config falls back
  // to name for display_name; image/address simply render blank).
  booking_display_name?:  string | null;
  booking_image_url?:     string | null;
  address_line?:          string | null;
  postal_code?:           string | null;
  city?:                  string | null;
  // Deep-link handle. Server normalizes to lowercase a-z/0-9/hyphen and rejects
  // a collision with another location (slug_taken). Empty string clears to NULL.
  slug?:                  string | null;
  // POS reader (tmr_...). Empty string clears (label clears along with it).
  pos_reader_id?:         string | null;
  pos_reader_label?:      string | null;
  // Must be http(s); empty string clears to NULL. Rejected server-side
  // otherwise, because the value lands in an href inside an email.
  google_review_url?:      string | null;
  tripadvisor_review_url?: string | null;
  tripadvisor_review_url_en?: string | null;
}

export async function editLocation(
  operatorId: string,
  input:      EditLocationInput,
): Promise<void> {
  const { error } = await supabase
    .schema('booking')
    .rpc('fn_admin_edit_location', {
      p_operator_id:           operatorId,
      p_location_id:           input.locationId,
      p_booking_enabled:       input.booking_enabled ?? null,
      p_booking_listed:        input.booking_listed ?? null,
      p_booking_number_prefix: input.booking_number_prefix ?? null,
      p_booking_display_name:  input.booking_display_name ?? null,
      p_booking_image_url:     input.booking_image_url ?? null,
      p_address_line:          input.address_line ?? null,
      p_postal_code:           input.postal_code ?? null,
      p_city:                  input.city ?? null,
      p_slug:                  input.slug ?? null,
      p_pos_reader_id:         input.pos_reader_id ?? null,
      p_pos_reader_label:      input.pos_reader_label ?? null,
      p_google_review_url:      input.google_review_url ?? null,
      p_tripadvisor_review_url: input.tripadvisor_review_url ?? null,
      p_tripadvisor_review_url_en: input.tripadvisor_review_url_en ?? null,
    });
  if (error) throw error;
}

// ---- Rooms list (used by Overrides + multi-room location detection) ------
export interface AdminRoomRow {
  id: string;
  name: string;
  location_id: string;
  max_capacity: number;
}

export async function listRooms(): Promise<AdminRoomRow[]> {
  const { data, error } = await supabase
    .schema('booking')
    .from('v_room_capacity')
    .select('id, name, max_capacity, location_id')
    .order('name', { ascending: true });
  if (error) throw error;
  return (data ?? []) as AdminRoomRow[];
}

// Update a room's base/default capacity (infra.rooms.max_capacity). Schedule
// overrides still layer on top per (room, date) — this is the fallback value.
// Server clamps to 1..100. Returns the persisted capacity.
export async function editRoomCapacity(
  operatorId:  string,
  roomId:      string,
  maxCapacity: number,
): Promise<number> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_edit_room_capacity', {
      p_operator_id:  operatorId,
      p_room_id:      roomId,
      p_max_capacity: maxCapacity,
    });
  if (error) throw error;
  return (data as { max_capacity: number }).max_capacity;
}
